/**
 * Portal Router
 *
 * Routes for portal.claimshieldpro.com
 * All routes require authentication.
 */
import {
  createRouter,
  createWebHistory,
  type NavigationGuardWithThis,
  type RouteRecordRaw,
} from 'vue-router'
import { PortalLayout, LoginView, ForgotPasswordView, ResetPasswordView, NotFoundView, setupGuards, useAuthStore, registerAppRouter, isPortalIdentityMode } from '@claimshield/shared'
import { useSeatReviewStore } from '../stores/seatReview'
import { useFleetEntitlementStore } from '../stores/fleetEntitlement'
import { useStateAccessStore } from '../stores/stateAccess'
import { BROKER_DISPATCH_ENABLED, DATAQ_CHALLENGES_ENABLED } from '@/config/features'
import { installRouteLoading } from './route-loading'

/** Keeps the DataQ claim surfaces unreachable by URL while the flow is gated,
 *  so hiding the affordances is not the only thing standing between a carrier
 *  and a pre-pilot wizard. See `@/config/features`. */
const dataqGate: NavigationGuardWithThis<undefined> = () =>
  DATAQ_CHALLENGES_ENABLED ? true : { name: 'dashboard' }

const dispatchGate: NavigationGuardWithThis<undefined> = () =>
  BROKER_DISPATCH_ENABLED ? true : { name: 'dashboard' }

/** The fleet-maintenance surfaces — shown in the sidebar AND reachable by URL
 *  only for a carrier whose `/v1/client/features` says `fleet-maintenance`.
 *  Both halves ride this one list (the sidebar via `hiddenNavRoutes` below,
 *  the routes via `fleetGate`), and `.claude/scripts/check_portal_nav_gates.py`
 *  fails the build if a row is ever hidden without a guard or guarded without
 *  being hidden. `/vehicles` is NOT here: it predates the program (2026-03)
 *  and carries applicant-declared vehicles for every carrier. */
const FLEET_ENTITLED_ROUTES = ['/repairs', '/invoice-review']
const AUDIT_ENTITLED_ROUTES = ['/audits']

/** Mirrors `dataqGate` for the fleet-maintenance routes. The store fails
 *  closed, so an unreachable or undeployed features route bounces to the
 *  dashboard rather than opening the surface. */
const fleetGate: NavigationGuardWithThis<undefined> = async () => {
  const fleet = useFleetEntitlementStore()
  await fleet.ensureLoaded()
  return fleet.fleetMaintenance ? true : { name: 'dashboard' }
}

const auditGate: NavigationGuardWithThis<undefined> = async () => {
  const features = useFleetEntitlementStore()
  await features.ensureLoaded()
  return features.auditCenter ? true : { name: 'dashboard' }
}

const stateAccessGate: NavigationGuardWithThis<undefined> = async () => {
  const stateAccess = useStateAccessStore()
  await stateAccess.ensureFeature()
  return stateAccess.enabled ? true : { name: 'dashboard' }
}

const routes: RouteRecordRaw[] = [
  // Auth routes (public)
  {
    path: '/login',
    name: 'login',
    component: () => isPortalIdentityMode() ? import('@views/PortalSignInView.vue') : Promise.resolve(LoginView),
    meta: { requiresAuth: false, layout: 'auth', title: 'Sign In | ClaimShield Portal' },
  },
  {
    path: '/signed-out', name: 'signed-out',
    component: () => import('@views/PortalSignedOutView.vue'),
    meta: { requiresAuth: false, layout: 'auth', title: 'Signed Out | ClaimShield Portal' },
  },
  {
    path: '/choose-carrier', name: 'choose-carrier',
    component: () => import('@views/PortalCarrierView.vue'),
    meta: { requiresAuth: false, layout: 'auth', title: 'Choose Carrier | ClaimShield Portal' },
  },

  // Onboarding routes (public - token-based auth, self-contained layout)
  {
    path: '/proposal/:token',
    name: 'proposal',
    component: () => import('@views/ProposalView.vue'),
    meta: { requiresAuth: false, title: 'Proposal | ClaimShield' },
  },
  {
    path: '/sign-agreement/:token',
    name: 'sign-agreement',
    component: () => import('@views/SignAgreementView.vue'),
    meta: { requiresAuth: false, title: 'Sign Agreement | ClaimShield' },
  },
  // Agreement-token EFT form (SignAgreementView routes here for eft_only
  // agreements and post-sign transitions). This must render EftFormView —
  // a past redirect to the proposal route sent client_agreements tokens to
  // ProposalView, which looks up the proposals table and 404s ("Unable to
  // Load Proposal", 2026-07-09 incident). Proposal-token flows never link
  // here; ProposalView collects EFT inline.
  {
    path: '/eft/:token',
    name: 'eft',
    component: () => import('@views/EftFormView.vue'),
    meta: { requiresAuth: false, title: 'EFT Authorization | ClaimShield' },
  },
  {
    path: '/booking/:token',
    name: 'booking',
    component: () => import('@views/BookingView.vue'),
    meta: { requiresAuth: false, title: 'Book Welcome Call | ClaimShield' },
  },
  {
    path: '/s/:token',
    name: 'shared-driver-file',
    component: () => import('@views/SharedDriverFileView.vue'),
    meta: { requiresAuth: false, title: 'Driver File | ClaimShield' },
  },
  // Carrier-wide DOT audit pass. Sibling of /s/:token — same public, login-less
  // shape, but scoped to a whole carrier's driver files. Its absence is why the
  // link the ops button hands out rendered a 404 on 2026-09-10.
  {
    path: '/audit/:token',
    name: 'client-audit-pass',
    component: () => import('@views/ClientAuditPassView.vue'),
    // On a phone, opening a driver is a history entry (#d<index>) so the
    // phone's Back returns to the roster. That is not a new page: keep the
    // roster's scroll position under it.
    meta: { requiresAuth: false, title: 'Driver Qualification Files | ClaimShield', keepScrollOnHash: true },
  },
  {
    path: '/a/:token',
    name: 'audit-token',
    component: () => import('@views/audit-center/AuditTokenView.vue'),
    meta: { requiresAuth: false, title: 'Audit | ClaimShield', keepScrollOnHash: true },
  },
  // Self-service applicant prescreen form (public — tokenized, no login).
  {
    path: '/careers/prescreen/:token',
    name: 'careers-prescreen',
    component: () => import('@views/PrescreenFormView.vue'),
    meta: { requiresAuth: false, title: 'Pre-Interview Questions | ClaimShield' },
  },
  {
    path: '/approvals/:token',
    name: 'phase-approval',
    component: () => import('@views/PhaseApprovalView.vue'),
    meta: { requiresAuth: false, title: 'Driver Approval | ClaimShield' },
  },
  {
    path: '/r/:token',
    name: 'remediation',
    component: () => import('@views/RemediationView.vue'),
    meta: { requiresAuth: false, public: true, title: 'Driver File Remediation | ClaimShield' },
  },
  {
    path: '/onboarding/expired',
    name: 'OnboardingExpired',
    component: () => import('@/views/OnboardingExpiredView.vue'),
    meta: { requiresAuth: false, public: true, title: 'Link Expired | ClaimShield' },
  },
  {
    path: '/onboarding/:token/complete',
    name: 'OnboardingComplete',
    component: () => import('@/views/OnboardingCompleteView.vue'),
    meta: { requiresAuth: false, public: true, title: 'Onboarding Complete | ClaimShield' },
  },
  {
    path: '/onboarding/:token',
    name: 'OnboardingWizard',
    component: () => import('@views/onboarding/OnboardingWizardView.vue'),
    meta: { requiresAuth: false, public: true, title: 'Onboarding | ClaimShield' },
  },
  // Standalone single onboarding step — deep-linked from the CS Report
  // "Action Needed" rows. :step is a slug (driver-list | driver-files | driver-app).
  {
    path: '/setup/:token/:step',
    name: 'OnboardingStep',
    component: () => import('@views/onboarding/OnboardingStepView.vue'),
    meta: { requiresAuth: false, public: true, title: 'Setup | ClaimShield' },
  },
  // Public driver-files uploader — deep-linked from the CS Report
  // "Driver File Setup" row. Works for any client (login-less, token-based).
  {
    path: '/upload/driver-files/:token',
    name: 'DriverFilesUpload',
    component: () => import('@views/onboarding/DriverFilesUploadView.vue'),
    meta: { requiresAuth: false, public: true, title: 'Upload Driver Files | ClaimShield' },
  },
  // Public driver-application branding page — a carrier puts their logo on
  // their own hiring form. Login-less on purpose: 5 client-role portal users
  // exist system-wide, and 2 of the 214 carriers receiving applications have
  // one, so the reachable audience is a link, not a login.
  {
    path: '/branding/:token',
    name: 'ApplicationBranding',
    component: () => import('@views/ApplicationBrandingView.vue'),
    meta: { requiresAuth: false, public: true, title: 'Driver Application Branding | ClaimShield' },
  },
  // Fleet setup wizard (public - magic-link token, no JWT)
  {
    path: '/fleet/setup/:token',
    name: 'FleetSetup',
    component: () => import('@views/FleetSetupView.vue'),
    meta: { requiresAuth: false, public: true, layout: 'auth', title: 'Fleet Setup | ClaimShield' },
  },
  // Legacy route — redirect to new wizard
  {
    path: '/onboarding-old/:token',
    name: 'onboarding-checklist',
    redirect: to => ({ name: 'OnboardingWizard', params: { token: to.params.token } }),
  },

  // Screening request routes (public - DOT-based, self-contained layout).
  // The DOT-less landings (legacy-form "this form has moved" links) MUST be
  // registered before the parametric routes — they ask for the DOT, generate
  // the direct link, and forward into the wizard.
  {
    path: '/request/mvr',
    name: 'mvr-request-landing',
    component: () => import('@views/ScreeningRequestLandingView.vue'),
    meta: { requiresAuth: false, title: 'MVR Request | ClaimShield', formKind: 'mvr' },
  },
  {
    path: '/request/mvr-psp',
    name: 'mvr-psp-request-landing',
    component: () => import('@views/ScreeningRequestLandingView.vue'),
    meta: { requiresAuth: false, title: 'MVR + PSP Request | ClaimShield', formKind: 'mvr_psp' },
  },
  {
    path: '/request/mvr/:dotNumber',
    name: 'mvr-request',
    component: () => import('@views/ScreeningRequestView.vue'),
    meta: { requiresAuth: false, title: 'MVR Request | ClaimShield' },
  },
  {
    path: '/request/mvr-psp/:dotNumber',
    name: 'mvr-psp-request',
    component: () => import('@views/ScreeningRequestView.vue'),
    meta: { requiresAuth: false, title: 'MVR + PSP Request | ClaimShield' },
  },
  // Survey routes (public - token-based, self-contained layout)
  {
    path: '/survey/:token',
    name: 'survey',
    component: () => import('@views/SurveyView.vue'),
    meta: { requiresAuth: false, title: 'Survey | ClaimShield' },
  },

  // Drug test location change (public - token-based, driver-facing)
  {
    path: '/test/location/:token',
    name: 'test-location-change',
    component: () => import('@views/test/TestLocationChange.vue'),
    meta: { requiresAuth: false, layout: 'minimal' as const, title: 'Find a Collection Site | ClaimShield' },
  },

  // FCRA adverse action dispute (public - token-based, driver-facing)
  {
    path: '/adverse-action/dispute/:token',
    name: 'AdverseActionDispute',
    component: () => import('@views/AdverseActionDisputeView.vue'),
    meta: { requiresAuth: false, public: true, title: 'Dispute Employment Decision | ClaimShield' },
  },

  // D&A policy re-sign (public - token-based, driver-facing)
  {
    path: '/resign/:token',
    name: 'PolicyResign',
    component: () => import('@views/PolicyResignView.vue'),
    meta: { requiresAuth: false, public: true, title: 'Drug & Alcohol Policy — ClaimShield' },
  },

  // Native document signing (public - token-based, generic envelope flow).
  // Served under /e-sign/* (NOT /sign/*): Caddy routes portal.claimshieldpro.com
  // /sign/* to the driver-application (frontend-apply) container, so /sign/* never
  // reaches this portal SPA. /e-sign/* falls through Caddy to frontend-portal.
  // Preview route declared before the catch-all token route so "preview" wins.
  {
    path: '/e-sign/preview/:token',
    name: 'SignEnvelopePreview',
    component: () => import('@views/sign/PreviewEnvelopeView.vue'),
    meta: { requiresAuth: false, public: true, title: 'Document Preview — ClaimShield' },
  },
  {
    path: '/e-sign/:token',
    name: 'SignEnvelope',
    component: () => import('@views/sign/SignEnvelopeView.vue'),
    meta: { requiresAuth: false, public: true, title: 'Sign Document — ClaimShield' },
  },

  // Safety policy acknowledgment (public - magic-link, driver-facing)
  {
    path: '/policy-ack/:token',
    name: 'PolicyAcknowledgment',
    component: () => import('@views/PolicyAcknowledgmentView.vue'),
    meta: { requiresAuth: false, public: true, title: 'Policy Acknowledgment — ClaimShield' },
  },

  // Staff read-only portal session, opened from ops for phone support.
  {
    path: '/view-as/redeem',
    name: 'view-as-redeem',
    component: () => import('@views/ViewAsDashboard.vue'),
    meta: { requiresAuth: false, public: true, title: 'Opening Client Dashboard | ClaimShield' },
  },
  {
    path: '/view-as',
    name: 'view-as-dashboard',
    component: () => import('@views/ViewAsDashboard.vue'),
    meta: { requiresAuth: false, public: true, title: 'Client Dashboard (View) | ClaimShield' },
  },

  {
    path: '/forgot-password',
    name: 'forgot-password',
    component: ForgotPasswordView,
    meta: { requiresAuth: false, layout: 'auth', title: 'Forgot Password | ClaimShield Portal' },
  },
  {
    path: '/reset-password',
    name: 'reset-password',
    component: ResetPasswordView,
    meta: { requiresAuth: false, layout: 'auth', title: 'Reset Password | ClaimShield Portal' },
  },

  // Portal routes (authenticated)
  {
    path: '/',
    component: PortalLayout,
    // The sidebar rides the same flag as the routes/buttons (bbe0e10d8) —
    // before this, a hidden feature kept its nav item and a click silently
    // bounced to the dashboard (Dugger's 08-25 report). A function, not an
    // object: RouterView calls it inside its render, so reading the
    // entitlement store here makes the sidebar follow the flag reactively —
    // including on a staff view-as switch, which navigates nowhere.
    props: () => {
      const fleet = useFleetEntitlementStore()
      return {
        hiddenNavRoutes: [
          ...(DATAQ_CHALLENGES_ENABLED ? [] : ['/challenges']),
          ...(BROKER_DISPATCH_ENABLED ? [] : ['/dispatch']),
          ...(fleet.fleetMaintenance ? [] : FLEET_ENTITLED_ROUTES),
          ...(fleet.auditCenter ? [] : AUDIT_ENTITLED_ROUTES),
        ],
      }
    },
    meta: { requiresAuth: true, roles: ['client', 'staff', 'admin'] },
    children: [
      {
        path: '',
        name: 'dashboard',
        component: () => import('@views/DashboardView.vue'),
        meta: { title: 'Dashboard | ClaimShield Portal' },
      },
      // Compliance tab retired 2026-07-06 — its per-driver compliance state
      // (MVR timeline, Samba score, auth forms, clearinghouse/examiner) now
      // lives on the driver file (/drivers/:id). Redirect keeps old bookmarks
      // and deep-links working. ComplianceView.vue is retained (unrouted) as
      // the port source until the fuller drivers merge lands.
      {
        path: 'compliance',
        redirect: { name: 'drivers' },
      },
      {
        path: 'safety-scores',
        name: 'safety-scores',
        component: () => import('@views/SafetyScoresView.vue'),
        meta: { title: 'Safety Scores | ClaimShield Portal' },
      },
      {
        // CSA drill-down explorer, opened from Safety Scores (CAB-workflow
        // style). Spec: knowledge/specs/2026-07-21-csa-insights-design.md
        path: 'csa-insights',
        name: 'csa-insights',
        component: () => import('@views/CSAInsightsView.vue'),
        meta: { title: 'CSA Insights | ClaimShield Portal' },
      },
      {
        path: 'accidents',
        name: 'accidents',
        component: () => import('@views/AccidentsView.vue'),
        meta: { title: 'Accident Registry | ClaimShield Portal' },
      },
      {
        path: 'challenges',
        name: 'challenges',
        component: () => import('@views/ChallengesView.vue'),
        meta: { title: 'Challenges | ClaimShield Portal' },
        beforeEnter: dataqGate,
      },
      {
        // The claim wizard (Task 3.2b) — a DRAFT case's guided claim flow.
        // Non-draft cases redirect back to /challenges from the view itself.
        path: 'challenges/:caseId/claim',
        name: 'challenge-claim',
        component: () => import('@views/challenges/ChallengeWizard.vue'),
        meta: { title: 'Challenge a Violation | ClaimShield Portal' },
        beforeEnter: dataqGate,
      },
      {
        path: 'roadside-inspections',
        name: 'roadside-inspections',
        component: () => import('@views/RoadsideInspectionsView.vue'),
        meta: { title: 'Roadside Inspections | ClaimShield Portal' },
      },
      {
        path: 'drivers',
        name: 'drivers',
        component: () => import('@views/DriversView.vue'),
        meta: { title: 'Drivers | ClaimShield Portal' },
      },
      {
        path: 'drivers/:id',
        name: 'driver-detail',
        component: () => import('@views/DriverDetailView.vue'),
        meta: { title: 'Driver Details | ClaimShield Portal' },
      },
      {
        path: 'vehicles',
        name: 'vehicles',
        component: () => import('@views/VehiclesView.vue'),
        meta: { title: 'Vehicles | ClaimShield Portal' },
      },
      {
        path: 'dispatch',
        name: 'dispatch',
        component: () => import('@views/DispatchView.vue'),
        meta: { title: 'Dispatch | ClaimShield Portal' },
        beforeEnter: dispatchGate,
      },
      // Fleet-maintenance surfaces — entitlement-gated, see FLEET_ENTITLED_ROUTES.
      {
        path: 'repairs',
        name: 'repairs',
        component: () => import('@views/RepairsView.vue'),
        meta: { title: 'Repairs | ClaimShield Portal' },
        beforeEnter: fleetGate,
      },
      {
        path: 'invoice-review',
        name: 'invoice-review',
        component: () => import('@views/InvoiceReviewView.vue'),
        meta: { title: 'Invoice Review | ClaimShield Portal' },
        beforeEnter: fleetGate,
      },
      {
        path: 'vehicles/:id/dvir/new',
        name: 'dvir-form',
        component: () => import('@views/DVIRFormView.vue'),
        meta: { title: 'New Inspection | ClaimShield Portal' },
      },
      {
        path: 'vehicles/:id/dvir/:dvirId',
        name: 'dvir-detail',
        component: () => import('@views/DVIRDetailView.vue'),
        meta: { title: 'Inspection Report | ClaimShield Portal' },
      },
      {
        path: 'vehicles/:id',
        name: 'vehicle-detail',
        component: () => import('@views/VehicleDetailView.vue'),
        meta: { title: 'Vehicle Details | ClaimShield Portal' },
      },
      {
        path: 'training',
        name: 'training',
        component: () => import('@views/TrainingView.vue'),
        meta: { title: 'Training | ClaimShield Portal' },
      },
      {
        path: 'billing',
        name: 'billing',
        component: () => import('@views/BillingView.vue'),
        meta: { title: 'Billing | ClaimShield Portal' },
      },
      // Services tab retired 2026-07-31: every element already had a better home
      // (Add a Driver → Drivers, Request Documents → driver detail, the plan
      // roster → Billing's "Your Services" card), and its ad-hoc Order MVR card
      // let clients pull records outside the compliance process. Redirected, NOT
      // deleted — the router's catch-all renders NotFoundView, so removing the
      // path would 404 old bookmarks and emailed links.
      // Spec: knowledge/specs/2026-07-31-portal-services-tab-retirement-design.md
      {
        path: 'services',
        redirect: { name: 'billing' },
      },
      {
        path: 'documents',
        name: 'documents',
        component: () => import('@views/DocumentsView.vue'),
        meta: { title: 'Documents | ClaimShield Portal' },
      },
      // Re-enabled 2026-08-13: the client-facing handbook backend is wired
      // (/v1/client/handbook/{versions,versions/:id,wizard-session} +
      // the disclaimer routes). Build/update goes through a handbook-scoped
      // onboarding-wizard session, ack flow intact (knowledge/handbook-builder/).
      {
        path: 'handbook',
        name: 'handbook',
        component: () => import('@views/HandbookBuilderView.vue'),
        meta: { title: 'Driver Handbook | ClaimShield Portal' },
      },
      // Temporarily disabled - re-enable when ready
      // {
      //   path: 'reports',
      //   name: 'reports',
      //   component: () => import('@views/ReportsView.vue'),
      //   meta: { title: 'Reports | ClaimShield Portal' },
      // },
      {
        path: 'state-access',
        name: 'state-access',
        component: () => import('@views/state-access/StateAccessView.vue'),
        beforeEnter: stateAccessGate,
        meta: { requiresAuth: true, roles: ['client'], title: 'State access | ClaimShield Portal' },
      },
      {
        path: 'settings',
        name: 'settings',
        component: () => import('@views/SettingsView.vue'),
        meta: { title: 'Settings | ClaimShield Portal' },
      },
      {
        path: 'contacts',
        name: 'contacts',
        component: () => import('@views/ContactsView.vue'),
        meta: { title: 'Notification Contacts | ClaimShield Portal' },
      },
      {
        // Client ↔ consultant thread (client-support Phase 2B). Reached from
        // the header entry point in PortalLayout. Staff/admin may open it in
        // view-as to read what the carrier sees; the view explains why they
        // cannot write from here, and the API 403s them anyway.
        path: 'messages',
        name: 'messages',
        component: () => import('@views/MessagesView.vue'),
        meta: { title: 'Messages | ClaimShield Portal' },
      },
      // Seat-review gate (spec 2026-08-03): blocking driver-count reconciliation
      // flow. Reached only via the beforeEach guard below when gated; staff
      // view-as and demo mode never land here (they get a banner instead).
      {
        path: 'seat-review',
        name: 'seat-review',
        component: () => import('@views/SeatReviewView.vue'),
        meta: { title: 'Seat Review | ClaimShield Portal' },
      },
      // Hidden for July-1 launch (Dugger, 2026-06-11 launch-surface review):
      // layout prototype, duplicate of '/' endpoints. Keep the code — useful R&D.
      // {
      //   path: 'dashboard-v2',
      //   name: 'dashboard-v2',
      //   component: () => import('@views/DashboardPrototypeView.vue'),
      //   meta: { title: 'Dashboard | ClaimShield Portal' },
      // },
      {
        path: 'audits',
        name: 'audits',
        component: () => import('@views/audit-center/AuditsView.vue'),
        beforeEnter: auditGate,
        meta: { requiresAuth: true, title: 'Audits | ClaimShield Portal' },
      },
      {
        path: 'audits/:id',
        name: 'audit-detail',
        component: () => import('@views/audit-center/AuditDetailView.vue'),
        beforeEnter: auditGate,
        meta: { requiresAuth: true, title: 'Audit | ClaimShield Portal' },
      },
      {
        path: 'dvir-defects',
        name: 'dvir-defects',
        component: () => import('@views/DVIRDefectsView.vue'),
        meta: { title: 'Reported Defects | ClaimShield Portal' },
      },
      {
        path: 'random-testing',
        name: 'random-testing',
        component: () => import('@views/RandomTestingView.vue'),
        meta: { title: 'Random Testing | ClaimShield Portal' },
      },
    ],
  },

  // Logout — clear auth then redirect to login
  {
    path: '/logout',
    name: 'logout',
    beforeEnter: () => {
      if (isPortalIdentityMode()) return true
      const { clearAuth } = useAuthStore()
      clearAuth()
      return '/login'
    },
    component: () => isPortalIdentityMode() ? import('@views/PortalLogoutView.vue') : Promise.resolve(LoginView),
    meta: { requiresAuth: false, layout: 'auth', title: 'Signing Out | ClaimShield Portal' },
  },

  // Development-only interactive review of the Clearinghouse client handoff.
  // It can open the real FMCSA Portal but never calls ClaimShield's backend.
  ...(import.meta.env.DEV ? [{
    path: '/__preview/clearinghouse-setup',
    name: 'clearinghouse-setup-preview',
    component: () => import('@views/test/ClearinghouseSetupPreview.vue'),
    meta: { requiresAuth: false, public: true, title: 'Clearinghouse Setup Preview | ClaimShield' },
  } satisfies RouteRecordRaw] : []),

  // Dev-only harness: renders each MatchedDriverPanel variant standalone so the
  // screening wizard's match screens can be reviewed without driving the wizard.
  ...(import.meta.env.DEV ? [{
    path: '/__harness/matched-driver',
    name: 'harness-matched-driver',
    component: () => import('@views/screening/MatchedDriverHarness.vue'),
    meta: { requiresAuth: false, public: true, title: 'Matched Driver Harness | ClaimShield' },
  } satisfies RouteRecordRaw] : []),

  // 404 catch-all
  {
    path: '/:pathMatch(.*)*',
    name: 'not-found',
    component: NotFoundView,
    meta: { requiresAuth: false, title: 'Not Found | ClaimShield Portal' },
  },
]

export const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition
    }
    // Same page, different hash, on a route that uses the hash for in-page
    // state: stay where the reader is.
    if (to.meta.keepScrollOnHash && to.path === from.path) {
      return false
    }
    return { top: 0 }
  },
})

// Register before auth guards and before app.use(router) starts initial navigation.
installRouteLoading(router)

router.beforeEach(async (to) => {
  if (!isPortalIdentityMode()) return true
  const sensitive = ['code', 'state', 'session_state', 'iss', 't']
  if (to.name !== 'view-as-redeem' && sensitive.some(key => key in to.query)) {
    const query = { ...to.query }
    for (const key of sensitive) delete query[key]
    return { path: to.path, query, hash: to.hash, replace: true }
  }
  if (to.name === 'view-as-dashboard' || to.name === 'view-as-redeem') return true
  const auth = useAuthStore()
  if (!auth.initialized) await auth.initialize()
  if (to.name === 'choose-carrier') return auth.portalSession ? true : { name: 'login' }
  if (to.name === 'login' && auth.portalSession && !auth.portalSession.carrier_id) {
    return { name: 'choose-carrier' }
  }
  if (to.meta.requiresAuth !== false && auth.portalSession && !auth.portalSession.carrier_id) {
    return { name: 'choose-carrier' }
  }
  if (to.name === 'training' && auth.portalSession?.capabilities.length === 1 &&
      auth.portalSession.capabilities[0] === 'billing') return { name: 'billing' }
  return true
})

// Setup navigation guards
setupGuards(router)

// Tell the shared axios interceptor about this router, so a 401 fired by a
// stale token on a public token route (/onboarding/:token and friends) does
// not evict the client to a /login they cannot pass (W5). Without this it
// falls back to reading Pinia internals, and then to an exact-match path list
// that cannot express a parameterised route at all.
registerAppRouter(router)

// Seat-review gate (spec 2026-08-03): a CLIENT with an open seat-review
// requirement is blocked into /seat-review until resolved. Staff view-as and
// demo mode are never gated (staff get a banner in PortalLayout instead) —
// gating on `auth.user?.role` (the REAL logged-in role, not `effectiveUser`)
// is deliberate: a staff member browsing view-as still has `user.role ===
// 'staff'` even though the portal renders as the viewed client.
router.beforeEach(async (to) => {
  if (to.meta.requiresAuth === false) return true
  const auth = useAuthStore()
  // Fleet entitlement — for EVERY authenticated navigation, staff included,
  // and before the client-only early return below: the layout's
  // `hiddenNavRoutes` reads this store, so it must be answered before the
  // sidebar first paints or the gated rows flash in and out. Memoized per
  // carrier; fails closed; never throws.
  await useFleetEntitlementStore().ensureLoaded()
  if (auth.user?.role !== 'client') return true
  if (auth.demoMode) return true
  const seatReview = useSeatReviewStore()
  await seatReview.ensureLoaded()
  if (seatReview.gated && to.name !== 'seat-review') return { name: 'seat-review' }
  if (!seatReview.gated && to.name === 'seat-review') return { name: 'dashboard' }
  return true
})
